import NavItem from './NavItem';
import { MenuLinks } from './config';
import PropTypes from 'prop-types';
import Logo from 'components/Logo';
import React, { useEffect, useState } from 'react';
import MyAvatar from 'components/MyAvatar';
import Scrollbars from 'components/Scrollbars';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
import { alpha, makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import { MAvatar } from '@material-extend';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CreateAppDialog from 'components/CreateApp/modal';
import {
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
  Drawer,
  Hidden,
  Typography,
  ListSubheader
} from '@material-ui/core';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const useStyles = makeStyles(theme => {
  const isLight = theme.palette.mode === 'light';

  return {
    drawer: {
      [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
        width: DRAWER_WIDTH
      }
    },
    drawerPaper: {
      width: DRAWER_WIDTH,
      background: theme.palette.background.default
    },
    subHeader: {
      ...theme.typography.overline,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(5),
      color: theme.palette.text.primary
    },
    account: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 2.5),
      margin: theme.spacing(1, 2.5, 5),
      borderRadius: theme.shape.borderRadiusSm,
      background: theme.palette.grey[isLight ? 200 : 800]
    },
    doc: {
      padding: theme.spacing(2.5),
      borderRadius: theme.shape.borderRadiusMd,
      backgroundColor: isLight
        ? alpha(theme.palette.primary.main, 0.08)
        : theme.palette.primary.lighter
    }
  };
});

// ----------------------------------------------------------------------

function reduceChild({ array, item, pathname, level }) {
  const key = item.href + level;

  if (item.items) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        open={Boolean(match)}
      >
        {renderNavItems({
          pathname,
          level: level + 1,
          items: item.items
        })}
      </NavItem>
    ];
  } else {
    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        href={item.href}
        icon={item.icon}
        info={item.info}
        title={item.title}
      />
    ];
  }
  return array;
}

function renderNavItems({ items, pathname, level = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (array, item) => reduceChild({ array, item, pathname, level }),
        []
      )}
    </List>
  );
}

NavBar.propTypes = {
  onCloseNav: PropTypes.func,
  isOpenNav: PropTypes.bool
};

function NavBar({ isOpenNav, onCloseNav, apps, organizations, organization, user, changeOrg, resetApps }) {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [ chooseOrgModalOpen, setChooseOrgModalOpen ] = useState(false);
  const [ createAppModalOpen, setCreateAppModalOpen ] = useState(false);
  const { profile } = global.currentUser;

  useEffect(() => {
    if (isOpenNav && onCloseNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleClickOpen = () => {
    setChooseOrgModalOpen(true);
  }

  const handleClose = () => {
    setChooseOrgModalOpen(false);
  };

  const handleClickChangeOrg = (org) => {
    if (org.id === organization.id) {
      return;
    }

    changeOrg(org);
  }

  const toggleCreateAppModal = () => {
    setCreateAppModalOpen(!createAppModalOpen);
  }

  const createNewApp = async () => {

    try {
      await resetApps();
      toggleCreateAppModal();
    } catch(e) {
      console.log(e);
    }
  }

  const renderContent = (
    <Scrollbars>
      <Box sx={{ px: 2.5, py: 3 }}>
        <RouterLink to="/">
          <Logo />
        </RouterLink>
      </Box>


      {organizations.length > 1 && (
        <Link
        underline="none"
        onClick={() => handleClickOpen()}
        >
          <div className={classes.account}>
            <MyAvatar profile={organization} profileType="Organization" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" color="textPrimary">
                {organization.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Change Organization
              </Typography>
            </Box>
          </div>
        </Link>
      )}

      {MenuLinks(apps).map(list => (
        <List
          disablePadding
          key={list.subheader}
          subheader={
            <ListSubheader
              disableSticky
              disableGutters
              className={classes.subHeader}
            >
              {list.subheader}
            </ListSubheader>
          }
        >
          {renderNavItems({
            items: list.items,
            pathname: pathname
          })}
        </List>
      ))}

      <Box sx={{ px: 5, pb: 3 }}>
        <Button
          onClick={toggleCreateAppModal}
          size="small"
          startIcon={<AddCircleIcon />}
        >
          Add A New App
        </Button>
      </Box>


      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <div className={classes.doc}>
          <Box
            component="img"
            alt="doc"
            src="/static/icons/ic_doc.svg"
            sx={{ width: 36, height: 36, mb: 2 }}
          />
          <Box
            component="h6"
            sx={{ mb: 1, typography: 'subtitle1', color: 'grey.800' }}
          >
            Hi, {profile.first_name}
          </Box>
          <Box sx={{ mb: 2, typography: 'body2', color: 'grey.600' }}>
            Need help?
            <br /> Please check our docs
          </Box>

          <Button
            fullWidth
            to="/"
            variant="contained"
            component={RouterLink}
          >
            Documentation
          </Button>
        </div>
      </Box>

    </Scrollbars>
  );

  return (
    <nav className={classes.drawer}>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          open={isOpenNav}
          variant="temporary"
          onClose={onCloseNav}
          classes={{ paper: classes.drawerPaper }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          open
          anchor="left"
          variant="persistent"
          classes={{ paper: classes.drawerPaper }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
      <Dialog open={chooseOrgModalOpen} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Choose Organization</DialogTitle>
        <List>
          {organizations.map((organization) => (
            <ListItem button onClick={() => {
              handleClickChangeOrg(organization)
            }} key={organization.id}>
              <ListItemAvatar>
                <MAvatar />
              </ListItemAvatar>
              <ListItemText primary={organization.name} />
            </ListItem>
          ))}
        </List>
      </Dialog>
      <CreateAppDialog
        open={createAppModalOpen}
        organization={organization}
        handleSuccess={createNewApp}
        handleClose={toggleCreateAppModal}
      />
    </nav>
  );
}

export default NavBar;
